import { isBefore, startOfDay } from 'date-fns';
import { z } from 'zod';

/**
 * 利用開始日が過去日付でないか判定する
 *
 * null / undefined は許容する（必須チェックは別の refine が担当する）。
 * 判定基準はブラウザのローカル時間（`new Date()`）。時刻部分の差異を無視するため
 * 両者を startOfDay で揃えてから比較する。
 *
 * DatePicker（ui/common/Form/FormItems/DatePicker）はテキスト直接入力不可で
 * カレンダークリック選択のみのため、UIの入力バイパスを防ぐためのものではない。
 * 実際の存在意義は「fromDate のレンダー時点鮮度」の問題への対策である。
 * CreateUserFormPresentation の `datePicker={{ fromDate: new Date() }}` は
 * レンダー時点で評価された固定値であり、以降のリアルタイム更新はされない。
 * フォームを開いたまま深夜0時をまたいで操作しないでいると、カレンダー側の
 * 選択不可境界が古い"今日"のままとなり、実際には過去日になった日付が
 * まだクリック可能な状態で残り得る。この refine は送信時に `new Date()` を
 * 再評価するため、レンダー鮮度のズレによる過去日紛れ込みを送信時点で正しく弾く。
 */
const isNotPastDate = (value: Date | null | undefined): boolean =>
  value === null || value === undefined || !isBefore(startOfDay(value), startOfDay(new Date()));

/**
 * 利用者追加フォームのバリデーションスキーマ
 *
 * パスワードの制約は旧 AddUserModal の validatePassword と互換
 * - 8文字以上
 * - 英数字混合
 *
 * billableFrom は常に必須（利用開始日フィールドは常時表示のため、必須バリデーションも常に適用する）。
 * 引数を取らないため関数ではなく定数として定義する。
 */
export const userFormSchema = z
  .object({
    clientId: z.string().min(1, '利用企業を選択してください。'),
    fullName: z.string().min(1, '氏名が入力されていません。'),
    email: z
      .string()
      .min(1, 'メールアドレスが入力されていません。')
      .regex(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'メールアドレスの形式が正しくありません。'
      ),
    externalUserId: z.string().optional(),
    password: z
      .string()
      .min(8, 'パスワードは8文字以上入力してください。')
      .regex(/[a-zA-Z]/, 'パスワードは英数字混合にしてください。')
      .regex(/[0-9]/, 'パスワードは英数字混合にしてください。'),
    passwordConfirmation: z.string().min(1, 'パスワード確認が入力されていません。'),
    // .refine(v => v !== null) は実行時チェックを追加するだけで billableFrom の型を
    // Date に絞り込まない（Zod の型推論は refine の述語からは narrow されない）ため、
    // .transform で明示的に Date へキャストし、以降（generateRequest 等）で
    // billableFrom が null を考慮した分岐を書かずに済むようにする。
    billableFrom: z
      .date({ invalid_type_error: '利用開始日を選択してください。' })
      .nullable()
      .refine(v => v !== null, { message: '利用開始日を選択してください。' })
      .refine(isNotPastDate, { message: '過去の日付は選択できません。' })
      .transform(v => v as Date),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    message: 'パスワードとパスワード（再入力）が一致しません。',
    path: ['passwordConfirmation'],
  });

export type CreateUserFormFieldValues = z.infer<typeof userFormSchema>;
